/**
 * One opt-in feature, as a card inside its BU's section.
 *
 * The state tag says "Ativa" or "Desativada (kill switch)" rather than draft/published on
 * purpose: a feature that is off is not unfinished, it is off for everyone right now.
 *
 * A **deleted** feature is rendered here too, greyed and dated, rather than in a list of its own:
 * the key it is still holding — `@@unique([tenantId, key])` does not look at `deletedAt` — only
 * means something next to the features using theirs. Its only action is "Restaurar"; editing or
 * deleting a row that is already deleted is not a thing to offer.
 *
 * @module Pages/BUs/OptInFeatures
 */

import { css } from '@emotion/css';
import { UTButton } from '@group-link-one/gl-ui/utilities';
import type { JSX } from 'react';
import type { OptInFeature } from '../../Types/OptInFeatures.types';
import { formatCount } from '../../Utils/adoptionFormat';
import { IconButton } from '@/Shared/components/IconButton/IconButton';
import { StatusTag } from '@/Shared/components/StatusTag/StatusTag';

const cardStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-m);
  padding: var(--space-ml);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-card);
  background: var(--color-surface);
  transition: box-shadow var(--duration-fast) var(--ease-standard);

  &:hover {
    box-shadow: var(--shadow-md);
  }
`;

const headerRowStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-s);
  flex-wrap: wrap;
`;

const keyStyle = css`
  font-family: var(--font-mono);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-secondary);
`;

const identityStyle = css`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`;

const nameStyle = css`
  font-size: 14px;
  font-weight: var(--font-weight-semibold);
`;

const descriptionStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
  text-wrap: pretty;
`;

const adoptionStyle = css`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const adoptionCountStyle = css`
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
`;

const adoptionHintStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
`;

const footerStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-s);
  padding-top: var(--space-s);
  border-top: 1px solid var(--color-divider);
  margin-top: auto;
`;

const spacerStyle = css`
  flex: 1;
`;

/**
 * Deleted rows read as switched off, not as broken.
 *
 * Dashed border and faded content say "this is not in play" at a glance; the content stays legible
 * because the whole point of showing it is to read the key it is holding.
 */
const deletedCardStyle = css`
  border-style: dashed;
  background: var(--color-surface-hover);

  &:hover {
    box-shadow: none;
  }
`;

const fadedStyle = css`
  opacity: 0.6;
`;

const deletedNoteStyle = css`
  margin: 0;
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
  text-wrap: pretty;
`;

/** `2026-09-22T15:02:51.598Z` → `22/09/2026 15:02`. Invalid or absent dates print nothing. */
const formatDeletedAt = (value: string | null | undefined): string => {
  if (!value) return '';
  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
};

export interface OptInFeatureCardProps {
  feature: OptInFeature;
  canEdit: boolean;
  canDelete: boolean;
  onAdoption: (feature: OptInFeature) => void;
  onEdit: (feature: OptInFeature) => void;
  onDelete: (feature: OptInFeature) => void;
  /** Absent when restoring is not on offer — the button then is not rendered at all. */
  onRestore?: (feature: OptInFeature) => void;
  isRestoring?: boolean;
}

export const OptInFeatureCard = ({
  feature,
  canEdit,
  canDelete,
  onAdoption,
  onEdit,
  onDelete,
  onRestore,
  isRestoring = false,
}: OptInFeatureCardProps): JSX.Element => {
  const isDeleted = Boolean(feature.deletedAt);
  const deletedAt = formatDeletedAt(feature.deletedAt);

  return (
    <article
      id={`opt-in-feature-card-${feature.id}`}
      className={`${cardStyle} ${isDeleted ? deletedCardStyle : ''}`}
    >
      <div className={headerRowStyle}>
        <span className={keyStyle}>{feature.key}</span>
        <div className={spacerStyle} />
        {isDeleted ? (
          <StatusTag id={`opt-in-feature-deleted-${feature.id}`} status='danger' size='sm' dot>
            {deletedAt ? `Excluída em ${deletedAt}` : 'Excluída'}
          </StatusTag>
        ) : (
          <StatusTag
            id={`opt-in-feature-state-${feature.id}`}
            status={feature.isActive ? 'success' : 'neutral'}
            dot
          >
            {feature.isActive ? 'Ativa' : 'Desativada'}
          </StatusTag>
        )}
      </div>

      <div className={`${identityStyle} ${isDeleted ? fadedStyle : ''}`}>
        <span className={nameStyle}>{feature.name}</span>
        {feature.description ? (
          <span className={descriptionStyle}>{feature.description}</span>
        ) : null}
      </div>

      {isDeleted ? (
        <p className={deletedNoteStyle}>
          Continua segurando a chave <strong>{feature.key}</strong>, então nenhuma funcionalidade
          nova pode usá-la. Restaurar devolve a funcionalidade como ela estava, com o estado de cada
          pessoa intacto.
        </p>
      ) : null}

      <div className={`${adoptionStyle} ${isDeleted ? fadedStyle : ''}`}>
        <span className={adoptionCountStyle}>
          {feature.enabledNow === undefined
            ? '—'
            : `${formatCount(feature.enabledNow)} com a funcionalidade ligada`}
        </span>
        <span className={adoptionHintStyle}>
          {feature.isActive
            ? 'Desativar devolve todo mundo ao comportamento antigo na hora.'
            : 'Ninguém está vendo a funcionalidade: todos estão no comportamento antigo.'}
        </span>
      </div>

      <div className={footerStyle}>
        {isDeleted ? (
          <>
            <div className={spacerStyle} />
            {onRestore ? (
              <UTButton
                fitContent
                variant='secondary'
                id={`opt-in-feature-restore-${feature.id}`}
                text='Restaurar'
                icon='Arrows.ArrowCounterClockwise'
                loading={isRestoring}
                onClick={() => {
                  onRestore(feature);
                }}
              />
            ) : null}
          </>
        ) : (
          <>
            <UTButton
              fitContent
              variant='secondary'
              id={`opt-in-feature-adoption-${feature.id}`}
              text='Ver adoção'
              icon='Misc.ChartLineUp'
              onClick={() => {
                onAdoption(feature);
              }}
            />
            <div className={spacerStyle} />
            {canEdit ? (
              <IconButton
                id={`opt-in-feature-edit-${feature.id}`}
                icon='Settings.PencilSimple'
                aria-label={`Editar ${feature.name}`}
                onClick={() => {
                  onEdit(feature);
                }}
              />
            ) : null}
            {canDelete ? (
              <IconButton
                id={`opt-in-feature-delete-${feature.id}`}
                icon='Misc.Trash'
                aria-label={`Excluir ${feature.name}`}
                variant='danger'
                onClick={() => {
                  onDelete(feature);
                }}
              />
            ) : null}
          </>
        )}
      </div>
    </article>
  );
};
